import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { firestore } from '../../backend'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { getCallableFunctionV2 } from '../../backend'

// Create or update webhook via Cloud Function (generates secret for new webhooks).
// Toute écriture sur /webhooks passe par les callables : les rules interdisent
// l'écriture client directe depuis le 26/08/2026 (BS-2455). Un `status: 'active'`
// explicite remet un webhook en erreur en service, compteur d'échecs à zéro.
export const saveWebhook = createAsyncThunk('webhooks/saveWebhook', async ({ id, data }) => {
	if (id) {
		// Update existing webhook via Cloud Function
		const updateWebhook = getCallableFunctionV2('updateWebhook')
		const result = await updateWebhook({
			webhookId: id,
			name: data.name,
			url: data.url,
			scopes: data.scopes,
			...(data.status ? { status: data.status } : {}),
		})
		return result.data
	} else {
		// Create new webhook via Cloud Function (to generate secret securely)
		const createWebhook = getCallableFunctionV2('createWebhook')
		const result = await createWebhook(data)
		return result.data
	}
})

// Fetch webhooks for a venue or venues group
export const fetchWebhooks = createAsyncThunk('webhooks/fetchWebhooks', async ({ venueId, venuesGroupId }, { dispatch }) => {
	try {
		let webhooksQuery

		if (venuesGroupId) {
			// Fetch webhooks for venues group
			webhooksQuery = query(
				collection(firestore, 'webhooks'),
				where('venuesGroupId', '==', venuesGroupId)
			)
		} else if (venueId) {
			// Fetch webhooks for specific venue
			webhooksQuery = query(
				collection(firestore, 'webhooks'),
				where('venueId', '==', venueId)
			)
		} else {
			// No venue or group specified
			dispatch(setWebhooks([]))
			return
		}

		const querySnapshot = await getDocs(webhooksQuery)
		const webhooks = querySnapshot.docs
			.map(doc => ({ ...doc.data(), id: doc.id }))
			.sort((a, b) => {
				// Sort by name alphabetically
				if (a.name > b.name) return 1
				if (a.name < b.name) return -1
				return 0
			})

		dispatch(setWebhooks(webhooks))
	} catch (error) {
		console.error('Error fetching webhooks:', error)
		throw error
	}
})

// Delete webhook via Cloud Function
export const deleteWebhook = createAsyncThunk('webhooks/deleteWebhook', async ({ id }) => {
	const deleteWebhookFn = getCallableFunctionV2('deleteWebhook')
	const result = await deleteWebhookFn({ webhookId: id })
	return result.data
})

// Regenerate webhook secret via Cloud Function
export const regenerateSecret = createAsyncThunk('webhooks/regenerateSecret', async ({ id }) => {
	const regenerateWebhookSecret = getCallableFunctionV2('regenerateWebhookSecret')
	const result = await regenerateWebhookSecret({ webhookId: id })
	return result.data
})

export const webhooksSlice = createSlice({
	name: 'webhooks',
	initialState: {
		value: [],
		status: 'idle',
		error: null,
	},
	reducers: {
		setWebhooks: (state, action) => {
			state.value = action.payload
		},
		setWebhookStatus: (state, action) => {
			state.status = action.payload
		},
	},
	extraReducers: {
		// Save webhook
		[saveWebhook.pending]: (state, action) => {
			state.status = 'loading'
		},
		[saveWebhook.fulfilled]: (state, action) => {
			state.status = 'succeeded'
		},
		[saveWebhook.rejected]: (state, action) => {
			state.status = 'failed'
			state.error = action.error.message
		},

		// Delete webhook
		[deleteWebhook.pending]: (state, action) => {
			state.status = 'loading'
		},
		[deleteWebhook.fulfilled]: (state, action) => {
			state.status = 'succeeded'
		},
		[deleteWebhook.rejected]: (state, action) => {
			state.status = 'failed'
			state.error = action.error.message
		},

		// Regenerate secret
		[regenerateSecret.pending]: (state, action) => {
			state.status = 'loading'
		},
		[regenerateSecret.fulfilled]: (state, action) => {
			state.status = 'succeeded'
		},
		[regenerateSecret.rejected]: (state, action) => {
			state.status = 'failed'
			state.error = action.error.message
		},
	},
})

// Selectors
export const getWebhooks = state => state.webhooks.value
export const getWebhookStatus = state => state.webhooks.status
export const getWebhookError = state => state.webhooks.error

export const { setWebhooks, setWebhookStatus } = webhooksSlice.actions

export default webhooksSlice.reducer
